import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './hooks/useAuth';
import GDPRCompliance from './components/compliance/GDPRCompliance';

// Admin Pages
import Layout from './components/Layout';
import AdminLogin from './pages/admin/Login';
import Dashboard from './pages/admin/Dashboard';
import Plans from './pages/admin/Plans';
import Users from './pages/admin/Users';
import Vouchers from './pages/admin/Vouchers';
import Sessions from './pages/admin/Sessions';
import AuditLogs from './pages/admin/AuditLogs';
import MobilePlatform from './pages/admin/MobilePlatform';
import Devices from './pages/admin/Devices';
// import CashOrders from './pages/admin/CashOrders'; // Cash payment removed

// User Pages
import UserLogin from './pages/user/Login';
import UserDashboard from './pages/user/Dashboard';

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (!requireAdmin && isAdmin) {
    return <Navigate to="/admin" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={isAdmin ? "/admin" : "/dashboard"} replace />;
  }

  return children;
};

function App() {
  // GDPR Compliance Handlers
  const handleGDPRAccept = (consentData) => {
    console.log('✅ GDPR Consent Accepted:', consentData);
    
    // Store consent data
    localStorage.setItem('gdpr_consent', JSON.stringify(consentData));
    localStorage.setItem('gdpr_accepted', 'true');
    
    // Reload to show the main portal
    window.location.reload();
  };

  const handleGDPRDecline = (declineData) => {
    console.log('❌ GDPR Consent Declined:', declineData);
    
    // Clear any existing consent
    localStorage.removeItem('gdpr_consent');
    localStorage.removeItem('gdpr_accepted');
    
    // Show decline message
    alert('Access denied. You must accept the terms and conditions to use this Wi-Fi service.');
  };

  // Check if GDPR consent is required
  const gdprAccepted = localStorage.getItem('gdpr_accepted') === 'true';
  const currentPath = window.location.pathname;
  
  // Skip GDPR for admin routes and test routes
  const skipGDPR = currentPath.startsWith('/admin') || 
                   currentPath === '/gdpr-test' ||
                   currentPath === '/gdpr-reset';
  
  // Show GDPR compliance page if not accepted and not on skip routes
  // TEMPORARILY DISABLED FOR DEBUGGING
   if (!gdprAccepted && !skipGDPR) {
     return (
       <GDPRCompliance 
         onAccept={handleGDPRAccept}
         onDecline={handleGDPRDecline}
        organizationName="Sateva Hotspot"
      />
     );
   }

  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route 
              path="/" 
              element={
                <PublicRoute>
                  <UserLogin />
                </PublicRoute>
              } 
            />
            
            <Route 
              path="/admin/login" 
              element={
                <PublicRoute>
                  <AdminLogin />
                </PublicRoute>
              } 
            />

            {/* User Routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <UserDashboard />
                </ProtectedRoute>
              } 
            />

            {/* Admin Routes */}
            <Route 
              path="/admin" 
              element={
                <ProtectedRoute requireAdmin>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="plans" element={<Plans />} />
              <Route path="users" element={<Users />} />
              <Route path="vouchers" element={<Vouchers />} />
              <Route path="sessions" element={<Sessions />} />
              <Route path="mobile" element={<MobilePlatform />} />
              {/* <Route path="cash-orders" element={<CashOrders />} /> Cash payment removed */}
              <Route path="devices" element={<Devices />} />
              <Route path="logs" element={<AuditLogs />} />
            </Route>

            {/* GDPR Test Routes */}
            <Route 
              path="/gdpr-test" 
              element={
                <GDPRCompliance 
                  onAccept={(data) => {
                    console.log('Test GDPR Accept:', data);
                    alert(`✅ Test Accepted!\n\nLanguage: ${data.language}\nSignature: ${data.signature}`);
                  }}
                  onDecline={(data) => {
                    console.log('Test GDPR Decline:', data);
                    alert(`❌ Test Declined!\n\nLanguage: ${data.language}`);
                  }}
                  organizationName="Test Sateva Hotspot"
                />
              } 
            />
            
            <Route 
              path="/gdpr-reset" 
              element={
                <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
                  <div className="bg-white rounded-lg shadow-lg p-6 text-center max-w-md">
                    <h2 className="text-xl font-bold mb-4">Reset GDPR Consent</h2>
                    <p className="text-gray-600 mb-4">Click to reset GDPR consent and test the flow again.</p>
                    <button 
                      onClick={() => {
                        localStorage.removeItem('gdpr_consent');
                        localStorage.removeItem('gdpr_accepted');
                        alert('GDPR consent reset! Redirecting to main page...');
                        window.location.href = '/';
                      }}
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
                    >
                      Reset & Test Again
                    </button>
                  </div>
                </div>
              } 
            />

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                style: {
                  background: '#10b981',
                },
              },
              error: {
                style: {
                  background: '#ef4444',
                },
              },
            }}
          />
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
